/**
 * Shared furniture for the setup task panels: the step frame, the copyable
 * row used by the DNS instructions, and the big choice card.
 *
 * Kept here so each panel is only its own logic.
 */

import React, { useState } from 'react';
import { Eyebrow, H1, Panel, Rule } from '../ui/kit';
import { cn } from '@/lib/utils';

/** The frame every task panel sits in: back link, title, rule, body. */
export function TaskShell({
  eyebrow,
  title,
  description,
  onBack,
  children,
}: {
  eyebrow: string;
  title: string;
  description?: React.ReactNode;
  onBack: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="mx-auto max-w-2xl">
      <button
        type="button"
        onClick={onBack}
        className="mb-8 font-mono text-[11px] uppercase tracking-eyebrow text-zinc-500 transition-colors hover:text-primary"
      >
        ← Back to setup
      </button>

      <Eyebrow className="mb-3">{eyebrow}</Eyebrow>
      <H1>{title}</H1>
      <Rule className="mt-5" />
      {description && <p className="mt-5 text-base leading-relaxed text-muted">{description}</p>}

      <div className="mt-8 space-y-5">{children}</div>
    </div>
  );
}

/** Monospace value with a copy button — DNS records, verification strings. */
export function CopyRow({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);

  return (
    <div className="flex items-center justify-between gap-3 border-b border-zinc-200 py-2.5 last:border-b-0">
      <div className="min-w-0">
        <div className="font-mono text-[10px] uppercase tracking-eyebrow text-zinc-400">{label}</div>
        <div className="truncate font-mono text-[13px] text-primary">{value}</div>
      </div>
      <button
        type="button"
        onClick={() => {
          void navigator.clipboard?.writeText(value);
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1600);
        }}
        className="shrink-0 rounded-full border border-zinc-300 px-3 py-1 font-mono text-[11px] uppercase tracking-eyebrow text-zinc-500 transition-colors hover:border-primary hover:text-primary"
      >
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
}

/** One of several routes through a task — picked, not filled in. */
export function ChoiceCard({
  title,
  description,
  selected,
  onSelect,
}: {
  title: string;
  description: string;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'w-full rounded-xl border p-4 text-left transition-colors',
        selected ? 'border-primary bg-white' : 'border-zinc-200 bg-white hover:border-zinc-400'
      )}
    >
      <div className="flex items-start gap-3">
        <span
          className={cn(
            'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
            selected ? 'border-primary' : 'border-zinc-300'
          )}
        >
          {selected && <span className="h-2 w-2 rounded-full bg-primary" />}
        </span>
        <div>
          <div className="text-sm font-semibold text-primary">{title}</div>
          <p className="mt-1 text-sm leading-snug text-muted">{description}</p>
        </div>
      </div>
    </button>
  );
}

/** The standard text input for these panels. */
export function TextInput({
  className,
  ...props
}: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className={cn(
        'w-full rounded-lg border border-zinc-300 bg-white px-3 py-2.5 text-sm text-primary outline-none transition-colors placeholder:text-zinc-400 focus:border-primary',
        className
      )}
    />
  );
}

/** Confirmation state shared by every panel once the step is behind them. */
export function TaskDone({
  title,
  detail,
  onBack,
  extra,
}: {
  title: string;
  detail: string;
  onBack: () => void;
  extra?: React.ReactNode;
}) {
  return (
    <Panel className="border-accent/30">
      <div className="p-6 text-center">
        <div className="mx-auto mb-4 flex h-10 w-10 items-center justify-center rounded-full bg-accent text-white">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={3}>
            <path strokeLinecap="round" strokeLinejoin="round" d="m5 13 4 4L19 7" />
          </svg>
        </div>
        <div className="font-display text-base font-bold tracking-tight text-primary">{title}</div>
        <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-muted">{detail}</p>
        {extra && <div className="mt-4">{extra}</div>}
        <button
          type="button"
          onClick={onBack}
          className="mt-5 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90"
        >
          Back to setup
        </button>
      </div>
    </Panel>
  );
}
